import { useState, useMemo } from 'react'
import { X, LogOut } from 'lucide-react'
import { authService } from '../services/authService'
import NavigationCategory from './navigation/NavigationCategory'
import { navigationCategories } from './navigation/navigationConfig'
import { User } from '@/types/navigation'
import ThemeToggle from './ThemeToggle'
import LanguageSwitcher from './LanguageSwitcher'

interface SidebarProps {
  onClose?: () => void
}

const Sidebar = ({ onClose }: SidebarProps) => {
  const user = authService.getCurrentUser() as User | null
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null)

  // Filter categories based on user role
  const filteredCategories = useMemo(() => {
    if (!user) return []
    return navigationCategories.filter(category => 
      category.roles.includes(user.role)
    )
  }, [user])

  const handleToggleCollapse = (categoryId: string) => {
    if (openCategoryId === categoryId) {
      // Si on clique sur le menu déjà ouvert, on le ferme
      setOpenCategoryId(null)
    } else {
      // Sinon, on ouvre ce menu (les autres se ferment automatiquement)
      setOpenCategoryId(categoryId)
    }
  }

  const isCategoryCollapsed = (categoryId: string) => {
    // Un menu est collapsed s'il n'est pas le menu ouvert
    return openCategoryId !== categoryId
  }

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200 dark:bg-gray-900 dark:border-gray-700">
      {/* Logo and close button */}
      <div className="flex items-center justify-between h-20 px-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <img
            src="/logo.svg"
            alt="Walomaydi3"
            className="h-12 w-12 object-contain"
          />
          <div className="ml-3">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Walomaydi3</h1>
            <p className="text-xs text-gray-500 hidden sm:block dark:text-gray-400">Administration</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 lg:hidden dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto" aria-label="Navigation principale">
        <div className="space-y-1">
          {filteredCategories.map(category => (
            <NavigationCategory
              key={category.id}
              category={category}
              user={user}
              onClose={onClose}
              isCollapsed={isCategoryCollapsed(category.id)}
              onToggleCollapse={handleToggleCollapse}
            />
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center dark:bg-primary-900/50">
              <span className="text-primary-700 font-medium dark:text-primary-300">
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user?.firstName || user?.email || 'Utilisateur'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {user?.role === 'admin' ? 'Administrateur' : 'Commerçant'}
                {user?.merchantContext && ` • ${user.merchantContext.permissionLevel}`}
                {user?.merchantContext?.merchantName && ` • ${user.merchantContext.merchantName}`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
        <button
          onClick={() => authService.logout()}
          className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Se déconnecter"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Déconnexion
        </button>
      </div>
    </div>
  )
}

export default Sidebar
